<template>
  <div>
    <div>
      <p>
        <span>Please enter your contact information below and click on 'Next'. We will send you a login code to your new contact information.</span> <br />
        <span>
          Login code will be valid for limited time. Enter the login code on the following screen and upon successful login code validation your contact information will be updated and you will be navigated
          to your RI DMV Online Customer profile. Your contact information will be used for future electronic Multi Factor Authentication (MFA).
        </span>
      </p>
      <div class="component-container" v-if="updateContact">
        <div v-if="updateContact.email">
          <ContactEmail :verify="verify" @doEmail="setEmail" @doResetVerify="resetVerify" :embedded="true" ref="contactEmailRef" @click.right.prevent :isRequired="false"/>
          <span class="app-error-msg" v-if="!isValidMyEmail">
            {{errMsgEmail}}
          </span>
          <ContactEmail :verify="verify" @doEmail="setConfirmEmail" @doResetVerify="resetVerify" :embedded="true" titleEmail="Confirm Email" userEmail="ConfirmEmail"
           ref="contactReenterEmailRef" @click.right.prevent :isRequired="false" @paste.prevent/>
          <span class="app-error-msg" v-if="!isValidConfirmEmail">
            {{errMsgConfirmEmail}}
          </span>
          <span class="app-error-msg" v-if="!validateEmail && errMsgCommon" >
            {{errMsgCommon}}
          </span>
        </div>
        <div v-if="updateContact.phone">
          <ContactPhone :verify="verify" @doPhone="setPhone" @doResetVerify="resetVerify" :embedded="true" ref="contactPhoneRef"
           @click.right.prevent titlePhone="Phone (Message and data rates may apply)"/>
          <span class="app-error-msg" v-if="!isValidMyPhone">
            {{errMsgPhone}}
          </span>
          <ContactPhone :verify="verify" @doPhone="setConfirmPhone" @doResetVerify="resetVerify" :embedded="true" titlePhone="Confirm Phone"
           ref="contactReenterPhoneRef" @click.right.prevent @paste.prevent/>
          <span class="app-error-msg" v-if="!isValidConfirmPhone">
            {{errMsgConfirmPhone}}
          </span>
          <span class="app-error-msg" v-if="!validatePhone && errMsgCommonPhone">
            {{errMsgCommonPhone}}
          </span>
        </div>  
      </div>
      <BaseButtonGroup>
        <button type="button" @click="handleRouting('previous')">Previous</button>
        <CustomButton @click="handleRouting('next')">
          Next
        </CustomButton>
      </BaseButtonGroup>
    </div>
  </div>
</template>


<script setup>
import * as COMMONS from '../../store/_commons'; 
import { useStore } from 'vuex'; 
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import ContactPhone from '@/components/contact/ContactPhone';
import ContactEmail from '@/components/contact/ContactEmail';
import CustomButton from '../CustomButton.vue';
import { ref, computed, onMounted  } from 'vue';
import { usePhoneFormatter } from '@/composables/usePhoneFormatter';

const myEmail = ref('');
const myPhone = ref('');
const myConfirmEmail = ref('');
const myConfirmPhone = ref('');
const verify = ref(true); 
const isComponentValid = ref(true);    // this parent's validations
const isPhoneValidated = ref(true);    // from ContactPhone
const isEmailValidated = ref(true);    // from ContactEmail
const isConfirmEmailValidated = ref(true);
const isConfirmPhoneValidated = ref(true);
const isValidMyEmail = ref(true);
const isValidConfirmEmail = ref(true);
const isValidMyPhone = ref(true);
const isValidConfirmPhone = ref(true);
const validateEmail = ref(true);
const validatePhone = ref(true);
const errMsgCommon = ref('');
const errMsgEmail = ref('');
const errMsgConfirmEmail = ref('');
const errMsgPhone = ref('');
const errMsgConfirmPhone = ref('');
const errMsgCommonPhone = ref('');

const contactEmailRef  = ref(null); 
const contactReenterEmailRef  = ref(null);
const contactPhoneRef  = ref(null);
const contactReenterPhoneRef  = ref(null);

// Use the phone formatter composable
const { formatPhoneForRims } = usePhoneFormatter();

// Vuex Store
const store = useStore(); 

const updateContact = computed(() => {
  return store.state.guestLogin.updateContact;
});

const contactInfo = computed(() => store.state.guestLogin.loginUserDetails);

//Lifecycle hook
onMounted(() => {
  window.scroll(0,0);
  document.body.setAttribute("tabindex", "1");
  document.body.focus();

  document.body.removeAttribute("tabindex");
  document.getElementById("app").scrollTop = 0;
});

// Methods
const getGuestSendMfaRequest = (data) => {
  store.dispatch(COMMONS.GUEST_SEND_MFA_REQUEST, data);
};

const handleRouting = async (info) => {
  if (info === 'previous') {
    store.state.guestLogin.contactUpdateStep = 1;
    return;
  }

  if (info !== 'next') return;

  await resetValidate();
  checkValidate();

  const hasEmail = myEmail.value !== '' && myConfirmEmail.value !== '';
  const hasPhone = myPhone.value !== '' && myConfirmPhone.value !== '';
  const hasContact = hasEmail || hasPhone;

  const checkSendMfa = async () => {
    if (!hasContact) return false;
    return await checkValidate();
  };

  const sendMfa = () => {
    const data = {
      email: myEmail.value,
      phone: formatPhoneForRims(myPhone.value)
    };
    getGuestSendMfaRequest(data);
  };

  const isPaperOtp = contactInfo.value?.isPaperOtpSent;

  if (!isPaperOtp) {
    checkValidate();
  }

  if (await checkSendMfa()) {
    sendMfa();
  }
};

// eslint-disable-next-line no-unused-vars
const continueToNextStep = async () => {
  if (ContactPhone.value) {
    if (ContactPhone.value.myPhone && ContactPhone.value.myPhone.length > 0) {
      await ContactPhone.value.checkPhone();
      isPhoneValidated.value = ContactPhone.value.invalidPhone;
      if (isPhoneValidated.value) {
        return; 
      }
    }
  } else {
    console.warn("ContactPhone ref not found. Skipping phone validation check.");
  }
  
  // Update parent's overall validation state
  isComponentValid.value = !isPhoneValidated.value; 
};

const checkValidate = async () => {
  if (updateContact.value.email) {
    await validateEmailFields();
  }

  if (updateContact.value.phone) {
    await validatePhoneFields();
  }

  return emailAndPhoneValidation();
};

// Email Validation
const validateEmailFields = async () => {
  const email = myEmail.value;
  const confirmEmail = myConfirmEmail.value;

  if (!email) {
    validateEmail.value = false;
    isValidMyEmail.value = false;
    errMsgEmail.value = 'Email is required';
  }

  if (!confirmEmail) {
    validateEmail.value = false;
    isValidConfirmEmail.value = false;
    errMsgConfirmEmail.value = 'Confirm email is required';
  }

  if (email && confirmEmail) {
    if (email === confirmEmail) {
      validateEmail.value = isValidMyEmail.value && isValidConfirmEmail.value;
    } else {
      validateEmail.value = false;
      errMsgCommon.value = 'Email and Confirm email values do not match';
    }
  }
};

// Phone Validation
const validatePhoneFields = async () => {
  if (contactPhoneRef.value) {
    await contactPhoneRef.value.checkPhone();
    isPhoneValidated.value = await contactPhoneRef.value.hasValidPhone();
  }

  if (contactReenterPhoneRef.value) {
    await contactReenterPhoneRef.value.checkPhone();
    isConfirmPhoneValidated.value = await contactReenterPhoneRef.value.hasValidPhone();
  }

  const phone = myPhone.value;
  const confirmPhone = myConfirmPhone.value;

  if (!phone) {
    validatePhone.value = false;
    isValidMyPhone.value = false;
    errMsgPhone.value = 'Phone is required';
  }

  if (!confirmPhone) {
    validatePhone.value = false;
    isValidConfirmPhone.value = false;
    errMsgConfirmPhone.value = 'Confirm phone is required';
  }

  if (phone && confirmPhone) {
    if (phone === confirmPhone) {
      validatePhone.value = isValidMyPhone.value && isValidConfirmPhone.value;
    } else {
      validatePhone.value = false;
      errMsgCommonPhone.value = 'Phone and Confirm phone values do not match';
    }
  }
};

const emailAndPhoneValidation = () => {
  const emailActive = updateContact.value.email;
  const phoneActive = updateContact.value.phone;

  if (phoneActive && !emailActive) {
    return validatePhone.value && isPhoneValidated.value && isConfirmPhoneValidated.value;
  }

  if (emailActive && !phoneActive) {
    return validateEmail.value && isEmailValidated.value && isConfirmEmailValidated.value;
  }

  // Both email and phone are active
  return (
    validateEmail.value &&
    isEmailValidated.value &&
    isConfirmEmailValidated.value &&
    validatePhone.value &&
    isPhoneValidated.value &&
    isConfirmPhoneValidated.value
  );
};

const resetValidate = async () => { 
  isValidMyEmail.value = true;
  isValidMyPhone.value = true;
  isValidConfirmEmail.value = true;
  isValidConfirmPhone.value = true;
  validateEmail.value = true;
  validatePhone.value = true;
  errMsgCommon.value = '';
  errMsgCommonPhone.value = '';
};

/**
 * Work differently from ModalContact's setEmail due to differences in props behavior.
 * @param data
 */
const setEmail = (data) => {
  myEmail.value = data.email; 
  isEmailValidated.value = data.isValid; // Update based on data from child component
};


const setConfirmEmail = (data) => {
  myConfirmEmail.value = data.email; 
  isConfirmEmailValidated.value = data.isValid; // Update based on data from child component
};

/**
 * Work differently from ModalContact's setPhone due to differences in props behavior.
 * @param data
 */
const setPhone = async (data) => {
  myPhone.value = data.phone; 
  isPhoneValidated.value = data.isValid; // Update based on data from child component
  isPhoneValidated.value = await contactPhoneRef.value.invalidPhone;
};


const setConfirmPhone = async (data) => {
  myConfirmPhone.value = data.phone; 
  isConfirmPhoneValidated.value = data.isValid; // Update based on data from child component
  isConfirmPhoneValidated.value = await contactReenterPhoneRef.value.invalidPhone;
};

const resetVerify = () => {
  console.log('(no action needed)');
};

defineExpose({
  continueToNextStep
});

</script>


<style lang="scss" scoped>
.err-msg{
  color: var(--ri-red);
}
.component-container {
  .app-error-msg{
    color: var(--ri-red);
    margin-top: 5px;
  }
  .app-contact-inputs {
    .app-contact-info{
      .app-user-email{
        min-width: 350px;
      }
    }
    .app-input-group{
      margin-bottom: 0em !important;
    }
  } 
}
button {
  margin-bottom: 1.125em;
  &:not(:last-child) {
    margin-right: 1em;
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}
</style>